import { useState, useEffect, useRef } from "react";
import { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { getAssociatedTokenAddress, createTransferInstruction, TOKEN_PROGRAM_ID } from '@solana/spl-token';
import {  
  createAssociatedTokenAccountInstruction,
  ASSOCIATED_TOKEN_PROGRAM_ID 
} from '@solana/spl-token';
import { getArenaPrice, calculateArenaAmount } from '../../birdeyeApi';

import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';

// Audio file paths
const AUDIO_PATHS = {
  BUTTON_CLICK: '/sounds/casino/button_click.mp3',
  WIN_SMALL: '/win.wav',
  WIN_MEDIUM: '/win.wav',
  WIN_BIG: '/win.wav',
  WIN_MEGA: '/win.wav',
  AMBIENT: '/sounds/casino/ambient_casino.mp3',
  ERROR: '/sounds/casino/error.mp3',
  COUNTDOWN: '/sounds/casino/countdown.mp3',
  DRUM_ROLL: '/sounds/casino/drum_roll.mp3',
  TICKET_PURCHASE: '/sounds/casino/coins_burst.mp3'
};

// ARENA Token Configuration
const ARENA_TOKEN_MINT = new PublicKey("HFqQ6xKTEyL2RSS7b8Ss5Sn4qgFqbL5b4rZmDhMhpkin");
const PROJECT_WALLET = new PublicKey("7SDrFeojsehbxuigUkJQVAoKDndh4LBuPVWsWwBd4JRm");
const ARENA_DECIMALS = 6;
const ALCHEMY_RPC_URL = 'https://solana-mainnet.g.alchemy.com/v2/pbGjxwq4DDspjqXb8DkO-Q4H-SzdtrLC';

// Simple Avatar component
const Avatar = ({ seed, size = 80, className = "" }) => {
  const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#6C5CE7'];
  const bgColor = colors[seed.charCodeAt(0) % colors.length];
  const initial = seed.charAt(0).toUpperCase();
  
  return (
    <div 
      className={`flex items-center justify-center font-bold text-white rounded-full ${className}`}
      style={{
        width: size,
        height: size,
        backgroundColor: bgColor,
        fontSize: size * 0.4
      }}
    >
      {initial}
    </div>
  );
};

export default function QuantumLottery() {
  const CURRENT_VERSION = "1.0.0";
  const LOTTERY_NUMBERS_RANGE = 50; // 0-49
  const NUMBERS_TO_PICK = 6;
  const TICKET_PRICE_USD = 0.2; // $0.2 per ticket

  const [arenaPrice, setArenaPrice] = useState(0);
  const [ticketPriceArena, setTicketPriceArena] = useState(0);
  const [isPriceLoading, setIsPriceLoading] = useState(true);

  const [jackpotWinnerCount, setJackpotWinnerCount] = useState(0);

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Add these hooks
const { connection } = useConnection();
const { publicKey, connected, disconnect, sendTransaction } = useWallet();
const { setVisible } = useWalletModal();

  // User state
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [balance, setBalance] = useState(0);

  const [winningTickets, setWinningTickets] = useState([]);
  const [hasTriggeredDraw, setHasTriggeredDraw] = useState(false);

  // Wallet state
  const [arenaBalance, setArenaBalance] = useState(0);
  const [solBalance, setSolBalance] = useState(0);
//   const [showWalletModal, setShowWalletModal] = useState(false);

  const [showWalletDropdown, setShowWalletDropdown] = useState(false);

  const [isWaitingAfterDraw, setIsWaitingAfterDraw] = useState(false);
  const [drawJustCompleted, setDrawJustCompleted] = useState(false);

  // Lottery state
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [myTickets, setMyTickets] = useState([]);
  const [potAmount, setPotAmount] = useState(0);
  const [lastDrawResults, setLastDrawResults] = useState(null);
  const [nextDrawTime, setNextDrawTime] = useState(null);
  const [timeUntilDraw, setTimeUntilDraw] = useState("");
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawAnimation, setDrawAnimation] = useState([]);
  const [winningNumbers, setWinningNumbers] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [myWinnings, setMyWinnings] = useState(0);
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [totalTicketsSold, setTotalTicketsSold] = useState(0);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState("");

  const [ticketQuantity, setTicketQuantity] = useState(1);
const [multipleTickets, setMultipleTickets] = useState([]);
const [showTicketQuantitySelector, setShowTicketQuantitySelector] = useState(false);
const MAX_TICKETS_PER_PURCHASE = 10; 

  // UI state
  const [showPaytable, setShowPaytable] = useState(false);
  const [showUserStats, setShowUserStats] = useState(false);
  const [showMyTickets, setShowMyTickets] = useState(false);
  const [showPastDraws, setShowPastDraws] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showCelebrationOverlay, setShowCelebrationOverlay] = useState(false);
  const [celebrationType, setCelebrationType] = useState('');
  const [celebrationData, setCelebrationData] = useState(null);
  const [quickPickAnimation, setQuickPickAnimation] = useState(false);

  const [expandedDraws, setExpandedDraws] = useState({});
const [showAllDraws, setShowAllDraws] = useState(false);

const PlusIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const MinusIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
  </svg>
);

const generateMultipleQuickPicks = (count) => {
  const tickets = [];
  for (let i = 0; i < count; i++) {
    const numbers = [];
    while (numbers.length < NUMBERS_TO_PICK) {
      const num = Math.floor(Math.random() * LOTTERY_NUMBERS_RANGE);
      if (!numbers.includes(num)) {
        numbers.push(num);
      }
    }
    tickets.push(numbers.sort((a, b) => a - b));
  }
  return tickets;
};

const toggleDrawExpansion = (drawId) => {
  setExpandedDraws(prev => ({
    ...prev,
    [drawId]: !prev[drawId]
  }));
};

const quickPickMultiple = () => {
  playSound('BUTTON_CLICK');
  setQuickPickAnimation(true);
  
  if (ticketQuantity === 1) {
    // Single ticket quick pick (existing logic)
    const numbers = [];
    while (numbers.length < NUMBERS_TO_PICK) {
      const num = Math.floor(Math.random() * LOTTERY_NUMBERS_RANGE);
      if (!numbers.includes(num)) {
        numbers.push(num);
      }
    }
    
    let index = 0;
    const animateInterval = setInterval(() => {
      if (index < numbers.length) {
        setSelectedNumbers(numbers.slice(0, index + 1));
        index++;
      } else {
        clearInterval(animateInterval);
        setQuickPickAnimation(false);
      }
    }, 100);
  } else {
    // Multiple tickets quick pick
    const tickets = generateMultipleQuickPicks(ticketQuantity);
    setMultipleTickets(tickets);
    setSelectedNumbers(tickets[0]); // Show first ticket in selection
    setQuickPickAnimation(false);
  }
};

const purchaseMultipleTicketsWithArena = async () => {
  // Determine which tickets to purchase
  const ticketsToPurchase = ticketQuantity === 1 
    ? [selectedNumbers] 
    : multipleTickets.length > 0 
      ? multipleTickets 
      : [selectedNumbers];
  
  // Validate all tickets
  for (let i = 0; i < ticketsToPurchase.length; i++) {
    if (ticketsToPurchase[i].length !== NUMBERS_TO_PICK) {
      handleError(`Ticket ${i + 1}: Please select exactly ${NUMBERS_TO_PICK} numbers`);
      return;
    }
  }
  
  if (!connected || !publicKey) {
    handleError("Please connect your wallet first!");
    playSound('ERROR');
    return;
  }

  if (isPriceLoading) {
    handleError("Please wait, fetching current price...");
    return;
  }
  
  if (!nextDrawTime) {
    handleError("No active lottery at the moment. Please wait for the next round.");
    return;
  }
  
  const totalCostArena = ticketPriceArena * ticketQuantity;
  const totalCostUSD = TICKET_PRICE_USD * ticketQuantity;
  
  if (arenaBalance < totalCostArena) {
    handleError(`Insufficient balance. You need ${totalCostArena} ARENA but have ${arenaBalance}`);
    return;
  }
  
  try {
    setIsPurchasing(true);
    setTransactionStatus("Preparing transaction...");
    playSound('BUTTON_CLICK');

    const connection = new Connection(ALCHEMY_RPC_URL, 'confirmed');
    const fromPubkey = publicKey;
    
    // Get token accounts
    const fromTokenAccount = await getAssociatedTokenAddress(
      ARENA_TOKEN_MINT,
      fromPubkey
    );
    
    const toTokenAccount = await getAssociatedTokenAddress(
      ARENA_TOKEN_MINT,
      PROJECT_WALLET
    );
    
    // Check if accounts exist
    const fromAccountInfo = await connection.getAccountInfo(fromTokenAccount);
    const toAccountInfo = await connection.getAccountInfo(toTokenAccount);
    
    if (!fromAccountInfo) {
      throw new Error("Your ARENA token account doesn't exist");
    }
    
    // Get actual balance
    const tokenBalance = await connection.getTokenAccountBalance(fromTokenAccount);
    
    if (parseFloat(tokenBalance.value.uiAmount) < totalCostArena) {
      throw new Error(`Insufficient balance. You have ${tokenBalance.value.uiAmount} ARENA but need ${totalCostArena}`);
    }
    
    // Create transaction
    const transaction = new Transaction();
    
    // If destination account doesn't exist, create it first
    if (!toAccountInfo) {
      console.log("Creating destination token account...");
      
      const createATAInstruction = createAssociatedTokenAccountInstruction(
        fromPubkey,
        toTokenAccount,
        PROJECT_WALLET,
        ARENA_TOKEN_MINT
      );
      
      transaction.add(createATAInstruction);
    }
    
    // Create transfer instruction with total amount
    const transferInstruction = createTransferInstruction(
      fromTokenAccount,
      toTokenAccount,
      fromPubkey,
      totalCostArena * Math.pow(10, ARENA_DECIMALS)
    );
    
    transaction.add(transferInstruction);
    
    setTransactionStatus("Waiting for wallet approval...");
    
    // Get recent blockhash
    const { blockhash } = await connection.getLatestBlockhash();
    transaction.recentBlockhash = blockhash;
    transaction.feePayer = fromPubkey;
    
    // Sign and send transaction
    const signature = await sendTransaction(transaction, connection);
    console.log("Transaction sent:", signature);
    
    setTransactionStatus("Confirming transaction...");
    
    // Wait for confirmation
    let confirmed = false;
    let retries = 0;
    const maxRetries = 30;
    
    while (!confirmed && retries < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      try {
        const status = await connection.getSignatureStatus(signature);
        
        if (status && status.value) {
          if (status.value.err) {
            throw new Error('Transaction failed: ' + JSON.stringify(status.value.err));
          }
          
          if (status.value.confirmationStatus === 'confirmed' || 
              status.value.confirmationStatus === 'finalized') {
            confirmed = true;
            console.log("Transaction confirmed:", signature);
          }
        }
      } catch (e) {
        console.log("Error checking status, will retry:", e);
      }
      
      retries++;
      setTransactionStatus(`Confirming transaction... (${retries}s)`);
    }
    
    if (!confirmed) {
      console.warn("Transaction not confirmed after timeout, proceeding anyway");
    }
    
    setTransactionStatus("Recording lottery tickets...");
    
    // Record all tickets in backend
    let successfulTickets = [];
    let failedTickets = 0;
    
    for (let i = 0; i < ticketsToPurchase.length; i++) {
      try {
        setTransactionStatus(`Recording ticket ${i + 1} of ${ticketsToPurchase.length}...`);
        
        const data = await apiCall('/api/lottery/purchase-ticket-arena', {
          method: 'POST',
          body: JSON.stringify({
            numbers: ticketsToPurchase[i],
            ticketPrice: ticketPriceArena,
            ticketPriceUSD: TICKET_PRICE_USD,
            arenaPrice: arenaPrice,
            walletAddress: publicKey.toString(),
            transactionSignature: signature,
            ticketIndex: i, // Add index for multi-ticket purchases
            totalTickets: ticketQuantity
          })
        });
        
        if (data.success) {
          successfulTickets.push(data.ticket);
        }
      } catch (error) {
        console.error(`Failed to record ticket ${i + 1}:`, error);
        failedTickets++;
      }
    }
    
    // Update local state
    if (successfulTickets.length > 0) {
      setMyTickets([...myTickets, ...successfulTickets]);
      setPotAmount(potAmount + (successfulTickets.length * ticketPriceArena * 0.9));
      
      // Clear selections
      setSelectedNumbers([]);
      setMultipleTickets([]);
      setTicketQuantity(1);
      
      // Show success
      setCelebrationType('success');
      setCelebrationData({ 
        message: `${successfulTickets.length} ticket${successfulTickets.length > 1 ? 's' : ''} purchased successfully!`,
        txSignature: signature.slice(0, 8) + '...' + signature.slice(-8),
        failedCount: failedTickets
      });
      setShowCelebrationOverlay(true);
      
      playSound('TICKET_PURCHASE');
      
      setTimeout(() => {
        setShowCelebrationOverlay(false);
      }, 4000);
    }
    
    // Update balances
    await updateBalances();
    
  } catch (error) {
    console.error('Purchase error:', error);
    
    let errorMessage = "Failed to purchase tickets";
    
    if (error.message) {
      if (error.message.includes('insufficient')) {
        errorMessage = "Insufficient ARENA balance";
      } else if (error.message.includes('User rejected')) {
        errorMessage = "Transaction cancelled";
      } else if (error.message.includes('No active lottery')) {
        errorMessage = "No active lottery found. Please wait for the next round.";
      } else {
        errorMessage = error.message;
      }
    }
    
    handleError(errorMessage);
    playSound('ERROR');
  } finally {
    setIsPurchasing(false);
    setTransactionStatus("");
  }
};

const ChevronDownIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronUpIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

  // Refs
  const audioRefs = useRef({});
  const ambientAudioRef = useRef(null);

  // Past draws history
  const [pastDraws, setPastDraws] = useState([]);
  const [isPastDrawsLoading, setIsPastDrawsLoading] = useState(false);

  // Add this helper function after your other helper functions (around line 350)
const detectWalletBrowser = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isInAppBrowser = 
    userAgent.includes('metamask') ||
    userAgent.includes('phantom') ||
    userAgent.includes('trust') ||
    userAgent.includes('coinbase') ||
    (window.ethereum && window.ethereum.isMetaMask) ||
    (window.solana && window.solana.isPhantom) ||
    window.ReactNativeWebView !== undefined;
    
  return isInAppBrowser;
};

  // Icon components
  const BanknotesIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );

  const TicketIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
    </svg>
  );

  const ClockIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  const SparklesIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );

  const FireIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
    </svg>
  );

  const ChartBarIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );

  const InformationCircleIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  const UserIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );

  const SpeakerWaveIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
    </svg>
  );

  const SpeakerXMarkIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
    </svg>
  );

  const LockClosedIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );

  const BeakerIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 018 10.172V5L8 4z" />
    </svg>
  );

  const WalletIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );

  const ArrowLeftIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );

  const XMarkIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  // Debug logging for wallet browsers
useEffect(() => {
  if (detectWalletBrowser()) {
    console.log('=== WALLET BROWSER DEBUG ===');
    console.log('Wallet browser detected:', {
      userAgent: navigator.userAgent,
      ethereum: !!window.ethereum,
      solana: !!window.solana,
      phantom: !!window.phantom,
      initialLoadComplete,
      nextDrawTime,
      showResults,
      isDrawing
    });
  }
}, [initialLoadComplete, nextDrawTime, showResults, isDrawing]);

  // Fetch ARENA price on mount and periodically
  useEffect(() => {
    const fetchArenaPrice = async () => {
      setIsPriceLoading(true);
      try {
        const price = await getArenaPrice();
        setArenaPrice(price);
        
        // Calculate ticket price in ARENA
        const arenaAmount = calculateArenaAmount(TICKET_PRICE_USD, price);
        setTicketPriceArena(arenaAmount);
      } catch (error) {
        console.error('Error fetching ARENA price:', error);
        // Fallback to default price
        setArenaPrice(0.001);
        setTicketPriceArena(1000);
      } finally {
        setIsPriceLoading(false);
      }
    };

    // Fetch immediately
    fetchArenaPrice();

    // Refresh price every 30 seconds
    const interval = setInterval(fetchArenaPrice, 30000);

    return () => clearInterval(interval);
  }, []);

  // Close wallet dropdown when clicking outside
useEffect(() => {
  const handleClickOutside = (event) => {
    if (showWalletDropdown && !event.target.closest('.relative')) {
      setShowWalletDropdown(false);
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, [showWalletDropdown]);

  // Audio setup
  useEffect(() => {
    Object.entries(AUDIO_PATHS).forEach(([key, path]) => {
      const audio = new Audio(path);
      audio.preload = 'auto';
      if (key === 'AMBIENT') {
        audio.loop = true;
        audio.volume = 0.3;
        ambientAudioRef.current = audio;
      } else {
        audio.volume = key.includes('WIN') ? 0.7 : 0.5;
        audioRefs.current[key] = audio;
      }
    });
    
    if (soundEnabled && ambientAudioRef.current) {
      ambientAudioRef.current.play().catch(e => console.log("Audio autoplay prevented:", e));
    }
    
    return () => {
      if (ambientAudioRef.current) {
        ambientAudioRef.current.pause();
        ambientAudioRef.current = null;
      }
      Object.values(audioRefs.current).forEach(audio => audio.pause());
      audioRefs.current = {};
    };
  }, []);

  // Sound toggle effect
  useEffect(() => {
    if (ambientAudioRef.current) {
      if (soundEnabled) {
        ambientAudioRef.current.play().catch(e => console.log("Audio play prevented:", e));
      } else {
        ambientAudioRef.current.pause();
      }
    }
  }, [soundEnabled]);

  // Play sound function
const playSound = (soundKey) => {
  if (!soundEnabled) return;
  const audioElement = audioRefs.current[soundKey];
  if (!audioElement) return;
  const sound = audioElement.cloneNode();
  sound.play().catch(e => console.log("Audio play prevented:", e));
};

  // Helper function to make API calls
// Replace your existing apiCall function (around line 350) with this:
const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };
  
  // Only add auth header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Add retry logic for wallet browsers
  const isWalletBrowser = detectWalletBrowser();
  let retries = isWalletBrowser ? 3 : 1;
  let lastError;
  
  while (retries > 0) {
    try {
      const response = await fetch(endpoint, {
        ...options,
        headers
      });
      
      // Handle 401 errors for lottery endpoints gracefully
      if (!response.ok) {
        // Allow lottery endpoints to work without auth for GET requests
        if (response.status === 401 && endpoint.includes('/api/lottery/') && (!options.method || options.method === 'GET')) {
          // Special handling for current endpoint
          if (endpoint.includes('/api/lottery/current')) {
            return { 
              success: true, 
              myTickets: [], 
              potAmount: 0, 
              nextDrawTime: null,
              totalParticipants: 0,
              totalTicketsSold: 0,
              lotteryId: null,
              initialRolloverAmount: 0
            };
          }
          // For other lottery GET endpoints, return empty success
          return { success: true };
        }
        
        const error = await response.json();
        throw new Error(error.error || 'API call failed');
      }
      
      return response.json();
    } catch (error) {
      lastError = error;
      retries--;
      
      if (retries > 0) {
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }
  
  throw lastError;
};

  // Extract user ID from token
  const extractUserIdFromToken = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload).userId;
    } catch (e) {
      return localStorage.getItem('userId');
    }
  };

  // Update wallet balances
const updateBalances = async () => {
  if (!publicKey) return;
  const address = publicKey.toString();
  
  try {
    const connection = new Connection(ALCHEMY_RPC_URL, 'confirmed');
    const pubkey = new PublicKey(address);
    
    // Get SOL balance
    const balance = await connection.getBalance(pubkey);
    setSolBalance(balance / LAMPORTS_PER_SOL);
    
    // Get ARENA token balance
    try {
      const tokenAccountAddress = await getAssociatedTokenAddress(
        ARENA_TOKEN_MINT,
        pubkey
      );
      
      // Check if token account exists
      const tokenAccountInfo = await connection.getAccountInfo(tokenAccountAddress);
      
      if (tokenAccountInfo) {
        // Get the parsed token balance
        const tokenBalance = await connection.getTokenAccountBalance(tokenAccountAddress);
        
        if (tokenBalance && tokenBalance.value) {
          const uiAmount = parseFloat(tokenBalance.value.uiAmount || 0);
          setArenaBalance(uiAmount);
          console.log("ARENA balance updated:", uiAmount);
        } else {
          setArenaBalance(0);
          console.log("Token balance response invalid");
        }
      } else {
        setArenaBalance(0);
        console.log("No ARENA token account found for this wallet");
      }
    } catch (error) {
      console.error('Error fetching ARENA balance:', error);
      setArenaBalance(0);
    }
  } catch (error) {
    console.error('Error fetching balances:', error);
    setSolBalance(0);
    setArenaBalance(0);
  }
};

// Update your wallet connection useEffect
useEffect(() => {
  if (connected && publicKey) {
    // Wait a bit for wallet browser to stabilize
    setTimeout(() => {
      updateBalances();
      // Force refresh lottery data after wallet connects to get tickets
      fetchLotteryData(true);
    }, 500);
  }
}, [connected, publicKey]);

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
          setError("Authentication required. Please log in.");
          setLoading(false);
          return;
        }
        
        const userId = extractUserIdFromToken(token);
        if (!userId) {
          setError("User ID not found. Please log in again.");
          setLoading(false);
          return;
        }
        
        const userData = await apiCall(`/api/users/${userId}`);
        setUser(userData);
        setBalance(userData.chips || 0);
      } catch (err) {
        console.error('Failed to load user data:', err);
        setError("Failed to load user data. Please refresh the page.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, []);

// Replace the existing useEffect that calls fetchLotteryData with this:
useEffect(() => {
  let mounted = true;
  let retryCount = 0;
  const maxRetries = 3;
  
  const fetchWithRetry = async () => {
    if (!mounted) return;
    
    try {
      await fetchLotteryData();
    } catch (error) {
      console.error('Error fetching lottery data:', error);
      
      // Retry logic for wallet browsers
      if (retryCount < maxRetries && mounted) {
        retryCount++;
        console.log(`Retrying fetch (${retryCount}/${maxRetries})...`);
        setTimeout(() => {
          if (mounted) fetchWithRetry();
        }, 2000 * retryCount); // Exponential backoff
      }
    }
  };
  
  // Initial fetch with slight delay for wallet browsers
  const isWalletBrowser = detectWalletBrowser();
  const initialDelay = isWalletBrowser ? 1000 : 0;
  
  setTimeout(() => {
    if (mounted) fetchWithRetry();
  }, initialDelay);
  
  // Set up interval for periodic updates
  const interval = setInterval(() => {
    if (!showResults && !drawJustCompleted && !isDrawing && mounted) {
      fetchLotteryData();
    }
  }, 30000); // Update every 30 seconds
  
  return () => {
    mounted = false;
    clearInterval(interval);
  };
}, [showResults, drawJustCompleted, isDrawing, connected, publicKey]); // Added connected and publicKey to dependencies


  // Countdown timer with automatic draw trigger
useEffect(() => {
  const timer = setInterval(() => {
    if (nextDrawTime) {
      const now = new Date();
      const drawTime = new Date(nextDrawTime);
      const diff = drawTime - now;
      
      if (diff <= 0) {
        setTimeUntilDraw("Drawing now!");
        
        // Automatically trigger draw when time is up
        if (!isDrawing && !showResults && !hasTriggeredDraw) {
          // Clear the interval to prevent multiple triggers
          clearInterval(timer);
          
          // Add a small delay to ensure all users see "Drawing now!"
          setTimeout(() => {
            startDrawAnimation();
          }, 2000);
        }
      } else {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setTimeUntilDraw(`${hours}h ${minutes}m ${seconds}s`);
      }
    }
  }, 1000);
  
  return () => clearInterval(timer);
}, [nextDrawTime, isDrawing, showResults, hasTriggeredDraw]);

// Update your fetchLotteryData function with this version:

// Replace your existing fetchLotteryData function with this:
const fetchLotteryData = async (forceUpdate = false) => {
  // Don't fetch if we're showing results or just completed a draw
  if ((showResults || drawJustCompleted) && !forceUpdate) {
    console.log('Skipping fetch - showing results or draw just completed');
    return;
  }
  
  try {
    // Build the URL with wallet address if available
    let url = '/api/lottery/current';
    const token = localStorage.getItem('token');
    
    // If wallet is connected, always pass wallet address as query param
    if (connected && publicKey) {
      url += `?walletAddress=${publicKey.toString()}`;
    }
    
    const data = await apiCall(url);
    
    if (data.success) {
      console.log('Lottery data received:', data);
      // Update lottery data
      setPotAmount(data.potAmount);
      setNextDrawTime(data.nextDrawTime);
      setTotalParticipants(data.totalParticipants || 0);
      setTotalTicketsSold(data.totalTicketsSold || 0);
      
      // Reset draw trigger flag if new lottery started
      if (data.nextDrawTime !== nextDrawTime) {
        setHasTriggeredDraw(false);
      }
      
      // Update tickets
      setMyTickets(data.myTickets || []);
      
      if (data.lastDraw) {
        setLastDrawResults(data.lastDraw);
      }
      
      // Mark initial load as complete
      setInitialLoadComplete(true);
    }
  } catch (error) {
    console.error('Failed to fetch lottery data:', error);
    
    // Only show "no active lottery" if we're not in a transition period
    if (error.message && error.message.includes('No active lottery')) {
      // In wallet browsers, wait longer before showing "no lottery" state
      const now = new Date();
      const timeSinceDraw = now - (window.lastLotteryError || 0);
      
      // Wait 10 seconds in wallet browsers (vs 30 seconds in normal browsers)
      const waitTime = detectWalletBrowser() ? 10000 : 30000;
      
      if (timeSinceDraw > waitTime) {
        console.log('No active lottery found - showing waiting state');
        
        setPotAmount(0);
        setNextDrawTime(null);
        setMyTickets([]);
        setTotalParticipants(0);
        setTotalTicketsSold(0);
        setInitialLoadComplete(true);
      } else {
        // Keep previous state for a bit to avoid flickering
        console.log('No active lottery found - keeping previous state temporarily');
      }
      
      window.lastLotteryError = now;
    } else {
      // For non-auth errors, still show the lottery UI
      setInitialLoadComplete(true);
    }
  }
};
const fetchPastDraws = async () => {
  try {
    setIsPastDrawsLoading(true);
    
    // Build URL with wallet address if connected
    let url = '/api/lottery/past-draws';
    if (connected && publicKey) {
      url += `?walletAddress=${publicKey.toString()}`;
    }
    
    const data = await apiCall(url);
    
    if (data.success) {
      setPastDraws(data.draws);
    }
  } catch (error) {
    console.error('Failed to fetch past draws:', error);
  } finally {
    setIsPastDrawsLoading(false);
  }
};

  const toggleNumber = (num) => {
    if (selectedNumbers.includes(num)) {
      setSelectedNumbers(selectedNumbers.filter(n => n !== num));
    } else if (selectedNumbers.length < NUMBERS_TO_PICK) {
      setSelectedNumbers([...selectedNumbers, num]);
      playSound('BUTTON_CLICK');
    }
  };

  const quickPick = () => {
    playSound('BUTTON_CLICK');
    setQuickPickAnimation(true);
    
    const numbers = [];
    while (numbers.length < NUMBERS_TO_PICK) {
      const num = Math.floor(Math.random() * LOTTERY_NUMBERS_RANGE);
      if (!numbers.includes(num)) {
        numbers.push(num);
      }
    }
    
    // Animate the selection
    let index = 0;
    const animateInterval = setInterval(() => {
      if (index < numbers.length) {
        setSelectedNumbers(numbers.slice(0, index + 1));
        index++;
      } else {
        clearInterval(animateInterval);
        setQuickPickAnimation(false);
      }
    }, 100);
  };

  const clearSelection = () => {
    playSound('BUTTON_CLICK');
    setSelectedNumbers([]);
  };

const purchaseTicketWithArena = async () => {
  if (selectedNumbers.length !== NUMBERS_TO_PICK) {
    handleError(`Please select exactly ${NUMBERS_TO_PICK} numbers`);
    return;
  }
  
  if (!connected || !publicKey) {
    handleError("Please connect your wallet first!");
    playSound('ERROR');
    return;
  }

  if (isPriceLoading) {
    handleError("Please wait, fetching current price...");
    return;
  }
  
  // Check if lottery is active before purchase
  if (!nextDrawTime) {
    handleError("No active lottery at the moment. Please wait for the next round.");
    return;
  }
  
  try {
    setIsPurchasing(true);
    setTransactionStatus("Preparing transaction...");
    playSound('BUTTON_CLICK');

    const connection = new Connection(ALCHEMY_RPC_URL, 'confirmed');
    const fromPubkey = publicKey;
    
    // Get token accounts
    const fromTokenAccount = await getAssociatedTokenAddress(
      ARENA_TOKEN_MINT,
      fromPubkey
    );
    
    const toTokenAccount = await getAssociatedTokenAddress(
      ARENA_TOKEN_MINT,
      PROJECT_WALLET
    );
    
    // Check if accounts exist
    const fromAccountInfo = await connection.getAccountInfo(fromTokenAccount);
    const toAccountInfo = await connection.getAccountInfo(toTokenAccount);
    
    if (!fromAccountInfo) {
      throw new Error("Your ARENA token account doesn't exist");
    }
    
    // Get actual balance
    const tokenBalance = await connection.getTokenAccountBalance(fromTokenAccount);
    
    if (parseFloat(tokenBalance.value.uiAmount) < ticketPriceArena) {
      throw new Error(`Insufficient balance. You have ${tokenBalance.value.uiAmount} ARENA but need ${ticketPriceArena}`);
    }
    
    // Create transaction
    const transaction = new Transaction();
    
    // If destination account doesn't exist, create it first
    if (!toAccountInfo) {
      console.log("Creating destination token account...");
      
      const createATAInstruction = createAssociatedTokenAccountInstruction(
        fromPubkey, // payer
        toTokenAccount, // ata
        PROJECT_WALLET, // owner
        ARENA_TOKEN_MINT // mint
      );
      
      transaction.add(createATAInstruction);
    }
    
    // Create transfer instruction with dynamic amount
    const transferInstruction = createTransferInstruction(
      fromTokenAccount,
      toTokenAccount,
      fromPubkey,
      ticketPriceArena * Math.pow(10, ARENA_DECIMALS) // Use dynamic price
    );
    
    transaction.add(transferInstruction);
    
    setTransactionStatus("Waiting for wallet approval...");
    
    // Get recent blockhash
    const { blockhash } = await connection.getLatestBlockhash();
    transaction.recentBlockhash = blockhash;
    transaction.feePayer = fromPubkey;
    
    // Sign and send transaction
    const signature = await sendTransaction(transaction, connection);
    console.log("Transaction sent:", signature);
    
    setTransactionStatus("Confirming transaction...");
    
    // Wait for confirmation with proper timeout
    let confirmed = false;
    let retries = 0;
    const maxRetries = 30; // 30 seconds max wait
    
    while (!confirmed && retries < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      try {
        const status = await connection.getSignatureStatus(signature);
        
        if (status && status.value) {
          if (status.value.err) {
            throw new Error('Transaction failed: ' + JSON.stringify(status.value.err));
          }
          
          if (status.value.confirmationStatus === 'confirmed' || 
              status.value.confirmationStatus === 'finalized') {
            confirmed = true;
            console.log("Transaction confirmed:", signature);
          }
        }
      } catch (e) {
        console.log("Error checking status, will retry:", e);
      }
      
      retries++;
      setTransactionStatus(`Confirming transaction... (${retries}s)`);
    }
    
    if (!confirmed) {
      console.warn("Transaction not confirmed after timeout, proceeding anyway");
    }
    
    setTransactionStatus("Recording lottery ticket...");
    
    // Try to record in backend with retries
    let backendSuccess = false;
    let backendRetries = 0;
    const maxBackendRetries = 3;
    let lastError = null;
    
    while (!backendSuccess && backendRetries < maxBackendRetries) {
      try {
        const data = await apiCall('/api/lottery/purchase-ticket-arena', {
          method: 'POST',
          body: JSON.stringify({
            numbers: selectedNumbers,
            ticketPrice: ticketPriceArena,
            ticketPriceUSD: TICKET_PRICE_USD,
            arenaPrice: arenaPrice,
            walletAddress: publicKey.toString(),
            transactionSignature: signature,
            ticketIndex: 0,      // ADD THIS LINE - for single ticket, index is 0
            totalTickets: 1      // ADD THIS LINE - for single ticket, total is 1
          })
        });
        
        if (data.success) {
          backendSuccess = true;
          
          // Update local state
          setMyTickets([...myTickets, data.ticket]);
          setPotAmount(data.currentPot);
          
          // Clear selected numbers
          setSelectedNumbers([]);
          
          // Show success
          setCelebrationType('success');
          setCelebrationData({ 
            message: 'Ticket purchased successfully with ARENA!',
            txSignature: signature.slice(0, 8) + '...' + signature.slice(-8)
          });
          setShowCelebrationOverlay(true);
          
          playSound('TICKET_PURCHASE');
          
          setTimeout(() => {
            setShowCelebrationOverlay(false);
          }, 3000);
        }
      } catch (error) {
        console.error(`Backend attempt ${backendRetries + 1} failed:`, error);
        lastError = error;
        
        // Check if it's a duplicate transaction error
        if (error.message && error.message.includes('already processed')) {
          // Transaction was already processed, consider it a success
          console.log("Transaction already processed, treating as success");
          backendSuccess = true;
          
          // Refresh lottery data to get the ticket
          await fetchLotteryData(true);
          
          setSelectedNumbers([]);
          setCelebrationType('success');
          setCelebrationData({ 
            message: 'Ticket already recorded!',
            txSignature: signature.slice(0, 8) + '...' + signature.slice(-8)
          });
          setShowCelebrationOverlay(true);
          playSound('TICKET_PURCHASE');
          
          setTimeout(() => {
            setShowCelebrationOverlay(false);
          }, 3000);
          
          break;
        }
        
        backendRetries++;
        if (backendRetries < maxBackendRetries) {
          await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2s before retry
          setTransactionStatus(`Recording ticket... (retry ${backendRetries})`);
        }
      }
    }
    
    // Update balances regardless of backend result
    await updateBalances();
    
    if (!backendSuccess) {
      // Transaction succeeded but backend failed
      console.error("Backend failed after all retries, but transaction succeeded");
      
      // Show partial success message
      setCelebrationType('warning');
      setCelebrationData({ 
        message: `Payment sent successfully! If your ticket doesn't appear, please contact support with TX: ${signature.slice(0, 16)}...`,
        txSignature: signature
      });
      setShowCelebrationOverlay(true);
      
      // Keep the message visible longer
      setTimeout(() => {
        setShowCelebrationOverlay(false);
      }, 10000);
      
      // Try to refresh lottery data
      setTimeout(() => {
        fetchLotteryData(true);
      }, 5000);
    }
    
  } catch (error) {
    console.error('Purchase error:', error);
    
    // More specific error messages
    let errorMessage = "Failed to purchase ticket";
    
    if (error.message) {
      if (error.message.includes('insufficient')) {
        errorMessage = "Insufficient ARENA balance";
      } else if (error.message.includes('User rejected')) {
        errorMessage = "Transaction cancelled";
      } else if (error.message.includes('No active lottery')) {
        errorMessage = "No active lottery found. Please wait for the next round.";
      } else {
        errorMessage = error.message;
      }
    }
    
    handleError(errorMessage);
    playSound('ERROR');
  } finally {
    setIsPurchasing(false);
    setTransactionStatus("");
  }
};

// Update your startDrawAnimation function with detailed logging:

const startDrawAnimation = async () => {
  if (isDrawing) {
    console.log('Already drawing, skipping...');
    return;
  }
  
  console.log('=== STARTING DRAW ANIMATION ===');
  console.log('Current time:', new Date().toISOString());
  
  setIsDrawing(true);
  setHasTriggeredDraw(true);
  setDrawJustCompleted(true); // Set the flag
  playSound('DRUM_ROLL');

  try {
    // First, trigger the check-draw to mark lottery as inactive
    console.log('Step 1: Calling check-draw endpoint...');
    const checkDrawResponse = await fetch('/api/lottery/check-draw', {
      method: 'POST',
      headers: {
        'x-cron-secret': 'i9o28393jl3-9103-01-348014jkjjklk4l2kl4920808sf8989s76q3555515528jmmfhuf74b'
      }
    });
    
    const checkDrawData = await checkDrawResponse.json();
    console.log('Check-draw response:', checkDrawData);
    
    if (!checkDrawResponse.ok) {
      throw new Error('Failed to process draw');
    }
    
    // Wait for database to update
    console.log('Step 2: Waiting 5 seconds for database to update...');
    await new Promise(resolve => setTimeout(resolve, 5000));
    
    // Now fetch the draw results
    console.log('Step 3: Fetching draw results...');
    console.log('Making request to draw-results at:', new Date().toISOString());
    
    // Build URL with wallet address if connected
    let url = '/api/lottery/draw-results';
    if (connected && publicKey) {
      url += `?walletAddress=${publicKey.toString()}`;
    }
    
    const data = await apiCall(url);
    console.log('Draw results response:', data);
    
    if (data.success) {
      const drawnNumbers = data.winningNumbers;
      setWinningNumbers(drawnNumbers);
      
      // Store jackpot winner count if multiple winners
      setJackpotWinnerCount(data.jackpotWinnerCount || 0);
      
      // Animate number reveal
      for (let i = 0; i < drawnNumbers.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        setDrawAnimation(prev => [...prev, drawnNumbers[i]]);
        playSound('BUTTON_CLICK');
      }
      
      // After animation, refresh lottery data to get updated tickets
      console.log('Refreshing lottery data to get updated tickets...');
      await fetchLotteryData(true);
      
      // Check winnings with split jackpot info
      setTimeout(() => {
        checkWinnings(drawnNumbers, data.jackpotWinnerCount);
        setIsDrawing(false);
        setShowResults(true);
        // Keep drawJustCompleted as true until user clicks Continue
      }, 2000);
    }
  } catch (error) {
    console.error('=== DRAW ERROR ===');
    console.error('Error details:', error);
    
    setIsDrawing(false);
    setHasTriggeredDraw(false);
    setDrawJustCompleted(false); // Reset on error
    handleError('Failed to process draw. Retrying...');
    
    // Wait before retry
    setTimeout(() => {
      if (!isDrawing && !showResults) {
        console.log('Retrying draw after error...');
        startDrawAnimation();
      }
    }, 10000);
  }
};

const checkWinnings = (drawnNumbers, jackpotWinnerCount = 0) => {
  let totalWin = 0;
  let winningTicketDetails = [];
  
  myTickets.forEach((ticket, index) => {
    const matchedNumbers = ticket.numbers.filter(num => drawnNumbers.includes(num));
    const matchCount = matchedNumbers.length;
    let ticketWinAmount = 0;
    
    if (matchCount >= 3) {
      if (matchCount === 3) {
        ticketWinAmount = ticketPriceArena; // Return bet
      } else if (matchCount === 4) {
        ticketWinAmount = ticketPriceArena * 2; // Double bet
      } else if (matchCount === 5) {
        ticketWinAmount = ticketPriceArena * 3; // Triple bet
      } else if (matchCount === 6) {
        // Split jackpot if multiple winners
        ticketWinAmount = jackpotWinnerCount > 1 
          ? Math.floor((potAmount * 0.5) / jackpotWinnerCount) 
          : potAmount * 0.5;
      }
      
      winningTicketDetails.push({
        ticketId: ticket.id,
        ticketIndex: index + 1,
        numbers: ticket.numbers,
        matchedNumbers: matchedNumbers,
        matchCount: matchCount,
        winAmount: ticketWinAmount,
        isSharedJackpot: matchCount === 6 && jackpotWinnerCount > 1,
        totalJackpotWinners: jackpotWinnerCount
      });
      
      totalWin += ticketWinAmount;
    }
  });
  
  setWinningTickets(winningTicketDetails);
  
  if (totalWin > 0) {
    // Play appropriate sound
    if (winningTicketDetails.some(t => t.matchCount === 6)) {
      playSound('WIN_MEGA');
    } else if (totalWin >= ticketPriceArena * 3) {
      playSound('WIN_BIG');
    } else {
      playSound('WIN_SMALL');
    }
  }
  
  setMyWinnings(totalWin);
};

const handleError = (errorMessage) => {
  setCelebrationType('error');
  setCelebrationData({ message: errorMessage });
  setShowCelebrationOverlay(true);
  setTimeout(() => setShowCelebrationOverlay(false), 3000);
};

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white font-mono flex items-center justify-center">
        <div className="text-center">
          <div className="w-24 h-24 border-4 border-violet-500 rounded-full relative flex items-center justify-center mx-auto">
            <div className="w-16 h-16 bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 rounded-full animate-pulse"></div>
            <div className="absolute inset-0 border-t-4 border-violet-500 rounded-full animate-spin"></div>
          </div>
          <div className="mt-6 text-lg">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-400 font-mono animate-pulse">
              QUANTUM LOTTERY
            </span>
            <div className="mt-2 text-sm text-gray-400">Loading lottery experience...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white font-mono relative -mt-16 overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-purple-500/20 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-pink-500/10 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 left-1/3 w-64 h-64 bg-blue-500/10 rounded-full filter blur-3xl"></div>
      </div>

      {/* Main content */}
      <div className="relative w-full max-w-lg mx-auto px-4 py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={() => {
              playSound('BUTTON_CLICK');
              window.history.back();
            }}
            className="bg-black/30 backdrop-blur-sm p-2 rounded-xl border border-gray-800 text-gray-400 hover:text-white hover:border-gray-700 transition-all"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>

          <div className="absolute left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse shadow-lg shadow-red-600/30">
            BETA
          </div>
          
<div className="flex items-center space-x-2">
  <button 
    onClick={() => {
      playSound('BUTTON_CLICK');
      setSoundEnabled(!soundEnabled);
    }}
    className="bg-black/30 backdrop-blur-sm p-2 rounded-xl border border-gray-800 text-pink-400 hover:text-pink-300 hover:border-gray-700 transition-all"
  >
    {soundEnabled ? (
      <SpeakerWaveIcon className="h-5 w-5" />
    ) : (
      <SpeakerXMarkIcon className="h-5 w-5" />
    )}
  </button>
  
  <div className="relative">
    <button
onClick={() => {
  if (connected) {
    setShowWalletDropdown(!showWalletDropdown);
  } else {
    setVisible(true);
  }
}}
className={`p-2 rounded-xl backdrop-blur-sm border transition-all ${
  connected 
    ? 'bg-green-900/20 border-green-700/50 hover:border-green-600' 
    : 'bg-red-900/20 border-red-700/50 hover:border-red-600'
}`}
    >
<WalletIcon className={`h-5 w-5 ${connected ? 'text-green-400' : 'text-red-400'}`} />
    </button>


    {/* Dropdown for connected wallet */}
    {showWalletDropdown && connected && publicKey && (
      <div className="absolute right-0 mt-2 bg-gray-900 rounded-xl border border-gray-700 shadow-2xl overflow-hidden z-50">
        <div className="px-4 py-3 border-b border-gray-800">
          <div className="text-xs text-gray-400">Connected</div>
          <div className="text-sm font-medium text-white">
            {publicKey.toString().slice(0, 4)}...{publicKey.toString().slice(-4)}
          </div>
          <div className="flex gap-3 mt-1 text-xs">
            <span className="text-yellow-400">{arenaBalance.toFixed(2)} ARENA</span>
            <span className="text-blue-400">{solBalance.toFixed(3)} SOL</span>
          </div>
        </div>
        
        <button
          onClick={() => {
            navigator.clipboard.writeText(publicKey.toString());
            setShowWalletDropdown(false);
            handleError('Wallet address copied!');
            playSound('BUTTON_CLICK');
          }}
          className="w-full px-4 py-3 text-left text-sm hover:bg-gray-800 transition-colors flex items-center space-x-2"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <span>Copy Address</span>
        </button>
        
        <button
onClick={() => {
  disconnect();
  setShowWalletDropdown(false);
}}
          className="w-full px-4 py-3 text-left text-sm hover:bg-gray-800 transition-colors flex items-center space-x-2 text-red-400"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span>Disconnect</span>
        </button>
      </div>
    )}
  </div>
  
  <button 
    onClick={() => {
      playSound('BUTTON_CLICK');
      setShowUserStats(true);
    }}
    className="bg-black/30 backdrop-blur-sm p-2 rounded-xl border border-gray-800 text-pink-400 hover:text-pink-300 hover:border-gray-700 transition-all"
  >
    <UserIcon className="h-5 w-5" />
  </button>
</div>
        </div>

        {/* Wallet Connection Card */}
{/* Wallet Icon - Minimal */}
        {/* <div className="mb-4 flex justify-end">
          <div className="relative">
            <button
              onClick={() => {
                if (walletAddress) {
                  setShowWalletDropdown(!showWalletDropdown);
                } else {
                  setShowWalletModal(true);
                }
              }}
              className={`p-3 rounded-xl backdrop-blur-sm border transition-all ${
                walletAddress 
                  ? 'bg-green-900/20 border-green-700/50 hover:border-green-600' 
                  : 'bg-red-900/20 border-red-700/50 hover:border-red-600'
              }`}
            >
              <WalletIcon className={`h-6 w-6 ${walletAddress ? 'text-green-400' : 'text-red-400'}`} />
            </button>

            {showWalletDropdown && walletAddress && (
              <div className="absolute right-0 mt-2 bg-gray-900 rounded-xl border border-gray-700 shadow-2xl overflow-hidden z-50">
                <div className="px-4 py-3 border-b border-gray-800">
                  <div className="text-xs text-gray-400">Connected</div>
                  <div className="text-sm font-medium text-white">
                    {walletAddress.slice(0, 4)}...{walletAddress.slice(-4)}
                  </div>
                  <div className="flex gap-3 mt-1 text-xs">
                    <span className="text-yellow-400">{arenaBalance.toFixed(2)} ARENA</span>
                    <span className="text-blue-400">{solBalance.toFixed(3)} SOL</span>
                  </div>
                </div>
                
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(walletAddress);
                    setShowWalletDropdown(false);
                    handleError('Wallet address copied!');
                    playSound('BUTTON_CLICK');
                  }}
                  className="w-full px-4 py-3 text-left text-sm hover:bg-gray-800 transition-colors flex items-center space-x-2"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span>Copy Address</span>
                </button>
                
                <button
                  onClick={() => {
                    disconnectWallet();
                    setShowWalletDropdown(false);
                  }}
                  className="w-full px-4 py-3 text-left text-sm hover:bg-gray-800 transition-colors flex items-center space-x-2 text-red-400"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Disconnect</span>
                </button>
              </div>
            )}
          </div>
        </div> */}

        {/* Security Badges */}
        {/* <div className="flex justify-center gap-2 mb-4">
          <div className="bg-blue-900/20 backdrop-blur-sm px-3 py-1.5 rounded-full border border-blue-800/50 text-blue-400 text-xs flex items-center">
            <LockClosedIcon className="h-3.5 w-3.5 mr-1" />
            <span>CERTIFIED FAIR</span>
          </div>
          <div className="bg-green-900/20 backdrop-blur-sm px-3 py-1.5 rounded-full border border-green-800/50 text-green-400 text-xs flex items-center">
            <BeakerIcon className="h-3.5 w-3.5 mr-1" />
            <span>QUANTUM SECURED</span>
          </div>
        </div> */}

{/* Main Lottery Info Card - Updated with loading state */}
{(!initialLoadComplete) ? (
  // Show loading state while fetching initial data
  <div className="bg-gradient-to-b from-gray-800/30 to-gray-900/30 backdrop-blur-md border border-gray-700/50 rounded-2xl p-8 mb-6 shadow-xl text-center">
    <div className="flex flex-col items-center space-y-4">
      <div className="relative w-16 h-16">
        <div className="absolute inset-0 rounded-full border-4 border-gray-700"></div>
        <div className="absolute inset-0 rounded-full border-4 border-yellow-400 border-t-transparent animate-spin"></div>
      </div>
      
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-yellow-400">
          Loading Lottery...
        </h3>
        <p className="text-sm text-gray-400">
          Connecting to lottery system...
        </p>
      </div>
    </div>
  </div>
) : nextDrawTime ? (
  // Show normal lottery info when active lottery exists
  <div className="bg-gradient-to-b from-gray-800/30 to-gray-900/30 backdrop-blur-md border border-gray-700/50 rounded-2xl p-4 mb-6 shadow-xl relative">
    <div className="space-y-3">
{/* Pot and Timer Row */}
<div className="flex items-center justify-between">
  {/* Pot Display */}
  <div>
    <div className="text-xs text-gray-400 uppercase">Pot</div>
    <div className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500">
      {potAmount.toLocaleString()} <span className="text-lg">ARENA</span>
    </div>
    {/* Show rollover indicator if pot has funds but no tickets sold yet */}
    {potAmount > 0 && totalTicketsSold === 0 && (
      <div className="text-xs text-green-400 mt-1 animate-pulse flex items-center">
        <SparklesIcon className="h-3 w-3 inline mr-1" />
        Includes rollover!
      </div>
    )}
  </div>
  
  {/* Timer */}
  <div className="text-right">
    <div className="text-xs text-gray-400 uppercase">Next Draw</div>
    <div className="text-xs font-semibold text-violet-300 flex items-center">
      <ClockIcon className="h-4 w-4 mr-1" />
      {timeUntilDraw}
    </div>
  </div>
</div>
      
      {/* Bottom Info Row */}
      <div className="flex items-center justify-between text-xs">
        {/* Participants */}
        <div className="text-gray-400">
          {totalTicketsSold > 0 && (
            <span>{totalTicketsSold} tickets • {totalParticipants} players</span>
          )}
        </div>
        
        {/* My Tickets */}
        {myTickets.length > 0 && (
          <div className="text-pink-400 font-medium">
            <TicketIcon className="h-3 w-3 inline mr-1" />
            {myTickets.length} {myTickets.length === 1 ? 'ticket' : 'tickets'}
          </div>
        )}
      </div>
      
      {/* Price Display */}
      {arenaPrice > 0 && (
        <>
          <div className="text-center text-xs text-green-400 pt-1 border-t border-gray-700/50">
            Ticket: {ticketPriceArena} ARENA (${(arenaPrice * ticketPriceArena).toFixed(2)})
          </div>
          <div className="text-center text-xs pt-1 flex items-center justify-center gap-1">
            <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-gray-500">Live:</span>
            <span className="text-yellow-400 font-medium">${arenaPrice.toFixed(8)}</span>
            <span className="text-gray-500">per ARENA</span>
          </div>
        </>
      )}
    </div>
    
    {/* Success/Error celebration overlay - only for non-win celebrations */}
    {showCelebrationOverlay && (celebrationType === 'success' || celebrationType === 'error') && (
      <div className="absolute inset-0 flex items-center justify-center z-50 rounded-2xl bg-black/80">
        {celebrationType === 'success' && celebrationData && (
          <div className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white py-3 px-4 rounded-xl shadow-lg border border-violet-400 text-center">
            <div className="text-base font-bold flex items-center justify-center">
              <TicketIcon className="h-5 w-5 mr-2" />
              Success!
            </div>
            <div className="text-sm mt-1">{celebrationData.message}</div>
          </div>
        )}
        
        {celebrationType === 'error' && celebrationData && (
          <div className="bg-red-700 text-white py-3 px-4 rounded-xl shadow-lg border border-red-500 text-center">
            <div className="text-base font-bold">ERROR</div>
            <div className="text-sm mt-1">{celebrationData.message}</div>
          </div>
        )}
      </div>
    )}
  </div>
) : (
  // Show waiting period UI when no active lottery
  !showResults && !isDrawing && (
    <div className="bg-gradient-to-b from-gray-800/30 to-gray-900/30 backdrop-blur-md border border-gray-700/50 rounded-2xl p-8 mb-6 shadow-xl text-center">
      <div className="flex flex-col items-center space-y-4">
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 rounded-full border-4 border-gray-700"></div>
          <div className="absolute inset-0 rounded-full border-4 border-yellow-400 border-t-transparent animate-spin"></div>
        </div>
        
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-yellow-400">
            Preparing Next Lottery
          </h3>
          <p className="text-sm text-gray-400">
            The next lottery round will begin shortly...
          </p>
          {/* Add refresh button for wallet browsers */}
          <button
            onClick={() => {
              setInitialLoadComplete(false);
              fetchLotteryData(true);
            }}
            className="text-xs text-gray-400 hover:text-white underline mt-2"
          >
            Refresh lottery status
          </button>
        </div>
      </div>
    </div>
  )
)}

        {/* Draw Animation Overlay */}
        {isDrawing && (
          <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500 mb-8">
                LIVE DRAW IN PROGRESS
              </h2>
              <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
                {Array(NUMBERS_TO_PICK).fill().map((_, index) => (
                  <div 
                    key={index}
                    className={`w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-bold ${
                      drawAnimation[index] !== undefined
                        ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white animate-bounce shadow-lg shadow-yellow-500/50'
                        : 'bg-gray-800 text-gray-600'
                    }`}
                  >
                    {drawAnimation[index] !== undefined ? drawAnimation[index] : '?'}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

{/* Results Overlay */}
        {showResults && winningNumbers.length > 0 && (
          <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
            <div className="bg-gradient-to-b from-gray-900 to-gray-800 rounded-2xl p-6 max-w-md w-full border border-gray-700 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500 mb-6">
                Draw Results
              </h2>
              
              {/* Winning Numbers */}
              <div className="mb-6">
                <p className="text-center text-gray-400 mb-4">Winning Numbers</p>
                <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
                  {winningNumbers.map(num => (
                    <div 
                      key={num}
                      className="w-14 h-14 rounded-2xl bg-gradient-to-r from-yellow-500 to-amber-500 flex items-center justify-center text-white font-bold text-lg shadow-lg"
                    >
                      {num}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Your Tickets Results */}
              {myTickets.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-center text-gray-300 mb-4">Your Tickets</h3>
                  <div className="space-y-3">
                    {myTickets.map((ticket, index) => {
                      const matchedNumbers = ticket.numbers.filter(num => winningNumbers.includes(num));
                      const isWinner = matchedNumbers.length >= 3;
                      
                      return (
                        <div 
                          key={ticket.id || index}
                          className={`p-4 rounded-xl border ${
                            isWinner 
                              ? 'bg-gradient-to-r from-green-900/30 to-emerald-900/30 border-green-500' 
                              : 'bg-gray-800/50 border-gray-700'
                          }`}
                        >
                          <div className="flex justify-between items-center mb-3">
                            <span className="text-sm font-semibold">
                              Ticket #{index + 1}
                            </span>
                            {isWinner && (
                              <span className="text-green-400 text-sm font-bold">
                                🎉 {matchedNumbers.length} matches!
                              </span>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-6 gap-2">
                            {ticket.numbers.map(num => {
                              const isMatched = winningNumbers.includes(num);
                              return (
                                <div 
                                  key={num}
                                  className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm ${
                                    isMatched
                                      ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white animate-pulse'
                                      : 'bg-gray-700 text-gray-300'
                                  }`}
                                >
                                  {num}
                                </div>
                              );
                            })}
                          </div>
                          
                          {isWinner && (
                            <div className="mt-3 text-sm">
                              <p className="text-green-400">
                                Won: {
                                  matchedNumbers.length === 3 ? `${ticketPriceArena} ARENA` :
                                  matchedNumbers.length === 4 ? `${ticketPriceArena * 2} ARENA` :
                                  matchedNumbers.length === 5 ? `${ticketPriceArena * 3} ARENA` :
                                  matchedNumbers.length === 6 ? (
                                    jackpotWinnerCount > 1 
                                      ? `${Math.floor(potAmount / jackpotWinnerCount)} ARENA (Split)`
                                      : `${potAmount} ARENA (JACKPOT!)`
                                  ) : ''
                                }
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* No tickets message */}
              {myTickets.length === 0 && (
                <div className="text-center mb-6 p-4 bg-gray-800/50 rounded-xl border border-gray-700">
                  <p className="text-gray-400">You didn't participate in this draw</p>
                </div>
              )}
              
              {/* Total Winnings */}
              {myWinnings > 0 && (
                <div className="text-center mb-6 p-4 bg-gradient-to-r from-yellow-900/30 to-amber-900/30 rounded-xl border border-yellow-600">
                  <p className="text-2xl font-bold text-yellow-400">
                    Total Won: {myWinnings} ARENA!
                  </p>
                  {winningTickets.some(t => t.isSharedJackpot) && (
                    <p className="text-sm text-yellow-300 mt-1">
                      Jackpot split between {jackpotWinnerCount} winners
                    </p>
                  )}
                  <p className="text-sm text-gray-300 mt-2">
                    Auto-payout to wallet
                  </p>
                </div>
              )}
              
<button
  onClick={() => {
    console.log('User clicked Continue button');
    
    // Reset all draw-related states
    setShowResults(false);
    setDrawAnimation([]);
    setWinningNumbers([]);
    setMyWinnings(0);
    setWinningTickets([]);
    setDrawJustCompleted(false); // Reset this flag
    setHasTriggeredDraw(false);
    
    // Clear current lottery data to show waiting state
    setPotAmount(0);
    setNextDrawTime(null);
    setMyTickets([]);
    setTotalParticipants(0);
    setTotalTicketsSold(0);
    
    // Wait a bit then check for new lottery
    setTimeout(() => {
      console.log('Fetching lottery data after Continue...');
      fetchLotteryData(true); // Force update
    }, 3000);
  }}
  className="w-full py-3 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-bold rounded-xl transition-all"
>
  Continue
</button>

            </div>
          </div>
        )}


{/* Number Selection Section */}
<div className="bg-gradient-to-b from-gray-800/30 to-gray-900/30 backdrop-blur-md rounded-2xl shadow-2xl overflow-hidden border border-gray-700/50 mb-6">
  <div className="bg-gradient-to-b from-gray-900/50 to-black/50 p-5">
    <h3 className="text-center text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400 mb-4">
      Pick {NUMBERS_TO_PICK} Numbers
    </h3>
    
    {/* Ticket Quantity Selector */}
    <div className="mb-4 bg-gray-800/50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-gray-400">Number of Tickets</span>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              if (ticketQuantity > 1) {
                setTicketQuantity(ticketQuantity - 1);
                setMultipleTickets([]);
                playSound('BUTTON_CLICK');
              }
            }}
            disabled={ticketQuantity <= 1}
            className="w-8 h-8 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all"
          >
            <MinusIcon className="h-4 w-4" />
          </button>
          
          <div className="px-4 py-1 bg-gray-700 rounded-lg min-w-[60px] text-center">
            <span className="text-lg font-bold text-white">{ticketQuantity}</span>
          </div>
          
          <button
            onClick={() => {
              if (ticketQuantity < MAX_TICKETS_PER_PURCHASE) {
                setTicketQuantity(ticketQuantity + 1);
                setMultipleTickets([]);
                playSound('BUTTON_CLICK');
              }
            }}
            disabled={ticketQuantity >= MAX_TICKETS_PER_PURCHASE}
            className="w-8 h-8 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all"
          >
            <PlusIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      {/* Price Summary */}
      <div className="text-center text-sm">
        <span className="text-gray-400">Total Cost: </span>
        <span className="text-yellow-400 font-bold">
          {(ticketPriceArena * ticketQuantity).toFixed(2)} ARENA
        </span>
        <span className="text-gray-400"> (${(TICKET_PRICE_USD * ticketQuantity).toFixed(2)})</span>
      </div>
      
      {/* Quick Pick All Button for Multiple Tickets */}
      {ticketQuantity > 1 && (
        <button
          onClick={() => {
            const tickets = generateMultipleQuickPicks(ticketQuantity);
            setMultipleTickets(tickets);
            setSelectedNumbers(tickets[0]);
            playSound('BUTTON_CLICK');
          }}
          className="w-full mt-3 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-sm font-bold rounded-lg flex items-center justify-center transition-all"
        >
          <SparklesIcon className="h-4 w-4 mr-2" />
          Quick Pick All {ticketQuantity} Tickets
        </button>
      )}
    </div>
    
    {/* Show Multiple Tickets Preview */}
    {ticketQuantity > 1 && multipleTickets.length > 0 && (
      <div className="mb-4 bg-gray-800/50 rounded-xl p-4">
        <div className="text-sm text-gray-400 mb-2">Your Tickets:</div>
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {multipleTickets.map((ticket, index) => (
            <div key={index} className="flex items-center justify-between p-2 bg-gray-700/50 rounded-lg">
              <span className="text-xs text-gray-400">#{index + 1}</span>
              <div className="flex gap-1">
                {ticket.map(num => (
                  <span key={num} className="text-xs font-bold text-white bg-violet-600/50 px-2 py-1 rounded">
                    {num}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
    
    {/* Selected Numbers Display (for single ticket or current ticket) */}
    {(ticketQuantity === 1 || multipleTickets.length === 0) && (
      <div className="mb-4 bg-gray-800/50 rounded-xl p-4">
        <div className="grid grid-cols-6 gap-2 min-h-[44px]">
          {selectedNumbers.length === 0 ? (
            <div className="col-span-6 text-center text-gray-500 text-sm">
              Tap numbers below to select
            </div>
          ) : (
            selectedNumbers.sort((a, b) => a - b).map(num => (
              <div 
                key={num}
                className="w-full aspect-square rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 flex items-center justify-center text-white font-bold animate-pulse shadow-lg"
              >
                {num}
              </div>
            ))
          )}
        </div>
      </div>
    )}
    
    {/* Number Grid - Only show for single ticket or if no multi-tickets generated */}
    {(ticketQuantity === 1 || multipleTickets.length === 0) && (
      <div className="grid grid-cols-5 gap-2 mb-4">
        {Array.from({ length: LOTTERY_NUMBERS_RANGE }, (_, i) => i).map(num => (
          <button
            key={num}
            onClick={() => toggleNumber(num)}
            disabled={!selectedNumbers.includes(num) && selectedNumbers.length >= NUMBERS_TO_PICK}
            className={`aspect-square rounded-xl flex items-center justify-center text-sm font-bold transition-all duration-200 ${
              selectedNumbers.includes(num)
                ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white transform scale-105 shadow-lg'
                : 'bg-gray-800/80 hover:bg-gray-700 text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed'
            }`}
          >
            {num}
          </button>
        ))}
      </div>
    )}
    
    {/* Action Buttons */}
    <div className="grid grid-cols-2 gap-3 mb-4">
      <button
        onClick={ticketQuantity === 1 ? quickPick : quickPickMultiple}
        disabled={quickPickAnimation}
        className="text-sm py-3 bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-500 hover:to-yellow-500 text-white font-bold rounded-xl flex items-center justify-center disabled:opacity-50 transition-all"
      >
        <SparklesIcon className="h-5 w-5 mr-2" />
        Quick Pick
      </button>
      
      <button
        onClick={() => {
          clearSelection();
          setMultipleTickets([]);
        }}
        className="text-sm py-3 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-gray-300 font-bold rounded-xl transition-all"
      >
        Clear All
      </button>
    </div>
    
    {/* Purchase Button */}
{/* Purchase Button - Update this in your code */}
<button
  onClick={ticketQuantity === 1 ? purchaseTicketWithArena : purchaseMultipleTicketsWithArena}
  disabled={
    (ticketQuantity === 1 && selectedNumbers.length !== NUMBERS_TO_PICK) ||
    (ticketQuantity > 1 && multipleTickets.length !== ticketQuantity && selectedNumbers.length !== NUMBERS_TO_PICK) ||
    !connected ||
    arenaBalance < (ticketPriceArena * ticketQuantity) ||
    isPurchasing ||
    isPriceLoading
  }
  className={`text-sm w-full py-4 rounded-xl font-bold text-lg tracking-wider transition-all duration-300 ${
    ((ticketQuantity === 1 && selectedNumbers.length === NUMBERS_TO_PICK) || 
     (ticketQuantity > 1 && (multipleTickets.length === ticketQuantity || selectedNumbers.length === NUMBERS_TO_PICK))) && 
    connected && 
    arenaBalance >= (ticketPriceArena * ticketQuantity) && 
    !isPurchasing && 
    !isPriceLoading
      ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white shadow-xl transform hover:scale-[1.02]'
      : 'bg-gray-800 text-gray-500 cursor-not-allowed'
  } flex items-center justify-center`}
>
  {isPurchasing ? (
    <>
      <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      {transactionStatus}
    </>
  ) : isPriceLoading ? (
    <>
      <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      Loading price...
    </>
  ) : (
    <>
      <TicketIcon className="h-5 w-5 mr-2" />
      Buy {ticketQuantity} Ticket{ticketQuantity > 1 ? 's' : ''} • {(ticketPriceArena * ticketQuantity).toFixed(2)} ARENA
    </>
  )}
</button>
    
    {!connected && (
      <p className="text-center text-yellow-400 text-xs mt-3">
        Connect wallet to purchase tickets
      </p>
    )}
  </div>
</div>

       {/* Bottom Navigation */}
       <div className="grid grid-cols-2 gap-3 mb-6">
         <button 
           onClick={() => {
             playSound('BUTTON_CLICK');
             setShowPaytable(true);
           }}
           className="text-sm flex items-center justify-center py-3 px-4 bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm hover:from-gray-700/50 hover:to-gray-800/50 text-violet-300 hover:text-violet-200 rounded-xl border border-gray-700/50 transition-all"
         >
           <InformationCircleIcon className="h-5 w-5 mr-2" />
           Prizes
         </button>
         
         <button 
           onClick={() => {
             playSound('BUTTON_CLICK');
             setShowMyTickets(true);
           }}
           className="text-sm flex items-center justify-center py-3 px-4 bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm hover:from-gray-700/50 hover:to-gray-800/50 text-pink-300 hover:text-pink-200 rounded-xl border border-gray-700/50 transition-all"
         >
           <TicketIcon className="h-5 w-5 mr-2" />
           My Tickets
         </button>
         
<button 
  onClick={async () => {
    playSound('BUTTON_CLICK');
    setShowPastDraws(true);
    if (pastDraws.length === 0 && !isPastDrawsLoading) {
      await fetchPastDraws();
    }
  }}
  className="text-sm flex items-center justify-center py-3 px-4 bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm hover:from-gray-700/50 hover:to-gray-800/50 text-cyan-300 hover:text-cyan-200 rounded-xl border border-gray-700/50 transition-all"
>
  <ChartBarIcon className="h-5 w-5 mr-2" />
  History
</button>
         
<button
  onClick={() => {
    playSound('BUTTON_CLICK');
    window.location.href = 'https://jup.ag/tokens/HFqQ6xKTEyL2RSS7b8Ss5Sn4qgFqbL5b4rZmDhMhpkin';
  }}
  className="text-sm flex items-center justify-center py-3 px-4 bg-gradient-to-br from-yellow-700/50 to-amber-800/50 backdrop-blur-sm hover:from-yellow-600/50 hover:to-amber-700/50 text-yellow-300 hover:text-yellow-200 rounded-xl border border-yellow-700/50 transition-all"
>
  <BanknotesIcon className="h-5 w-5 mr-2" />
  Buy $ARENA
</button>

       </div>

       {/* Modals */}
       {/* {showWalletModal && <WalletModal />} */}

       {/* Paytable Modal - Mobile Optimized */}
       {showPaytable && (
         <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 overflow-auto">
           <div className="bg-gradient-to-b from-gray-900 to-gray-800 backdrop-blur-lg rounded-2xl p-6 w-full max-w-sm border border-gray-700 max-h-[90vh] overflow-y-auto shadow-2xl">
             <div className="flex justify-between items-center mb-4">
               <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-pink-400 to-red-400">Prize Structure</h2>
               <button 
                 onClick={() => {
                   playSound('BUTTON_CLICK');
                   setShowPaytable(false);
                 }}
                 className="text-gray-300 hover:text-white p-2"
               >
                 <XMarkIcon className="h-5 w-5" />
               </button>
             </div>
             
<div className="space-y-3">
  <div className="bg-gradient-to-r from-yellow-900/30 to-amber-900/30 p-4 rounded-xl border border-yellow-700/50">
    <div className="flex items-center mb-2">
      <FireIcon className="h-6 w-6 text-yellow-400 mr-2" />
      <span className="text-yellow-300 font-bold">6 Matches - JACKPOT!</span>
    </div>
    <p className="text-gray-300 text-sm">Win 50% of the entire pot!</p> {/* ← CHANGE THIS */}
  </div>
  
  <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700">
    <div className="flex items-center justify-between mb-1">
      <span className="text-violet-300 font-semibold">5 Matches</span>
      <span className="text-green-400">3x Bet</span>
    </div>
    <p className="text-gray-400 text-xs">Triple your ticket price</p>
  </div>
  
  <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700">
    <div className="flex items-center justify-between mb-1">
      <span className="text-violet-300 font-semibold">4 Matches</span>
      <span className="text-green-400">2x Bet</span>
    </div>
    <p className="text-gray-400 text-xs">Double your ticket price</p>
  </div>
  
  <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700">
    <div className="flex items-center justify-between mb-1">
      <span className="text-violet-300 font-semibold">3 Matches</span>
      <span className="text-green-400">1x Bet</span>
    </div>
    <p className="text-gray-400 text-xs">Get your ticket price back</p>
  </div>
  
  <div className="mt-4 text-xs text-gray-400 text-center space-y-1">
    <p>Numbers can match in any order</p>
    <p>House takes 10% of pot for operations</p>
    <p>If no jackpot winner, remaining funds roll over</p> {/* ← CHANGE THIS TOO */}
    <p className="mt-2 text-yellow-400">Tickets cost $0.2 ({ticketPriceArena} ARENA)</p>
    <p className="text-green-400">Winners paid automatically</p>
  </div>
</div>
           </div>
         </div>
       )}

       {/* My Tickets Modal - Mobile Optimized */}
       {showMyTickets && (
         <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 overflow-auto">
           <div className="bg-gradient-to-b from-gray-900 to-gray-800 backdrop-blur-lg rounded-2xl p-6 w-full max-w-sm border border-gray-700 max-h-[90vh] overflow-y-auto shadow-2xl">
             <div className="flex justify-between items-center mb-4">
               <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-pink-400 to-red-400">My Tickets</h2>
               <button 
                 onClick={() => {
                   playSound('BUTTON_CLICK');
                   setShowMyTickets(false);
                 }}
                 className="text-gray-300 hover:text-white p-2"
               >
                 <XMarkIcon className="h-5 w-5" />
               </button>
             </div>
             
             {myTickets.length === 0 ? (
               <div className="text-center text-gray-400 py-12">
                 <TicketIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
                 <p className="mb-2">No tickets purchased yet</p>
                 <p className="text-sm">Buy tickets to participate!</p>
               </div>
             ) : (
               <div className="space-y-3">
                 {myTickets.map((ticket, index) => {
                   const winningTicket = winningTickets.find(wt => wt.ticketId === ticket.id);
                   const isWinner = !!winningTicket;
                   
                   return (
                     <div 
                       key={ticket.id || index} 
                       className={`p-4 rounded-xl border ${
                         isWinner 
                           ? 'bg-gradient-to-r from-green-900/30 to-emerald-900/30 border-green-500 animate-pulse' 
                           : 'bg-gray-800/50 border-gray-700'
                       }`}
                     >
                       <div className="flex justify-between items-center mb-3">
                         <span className="text-sm font-semibold">
                           Ticket #{index + 1}
                           {isWinner && (
                             <span className="ml-2 text-green-400">
                               🎉 WINNER!
                             </span>
                           )}
                         </span>
                       </div>
                       
                       <div className="grid grid-cols-6 gap-2">
                         {ticket.numbers.map(num => {
                           const isMatched = winningTicket?.matchedNumbers.includes(num);
                           return (
                             <div 
                               key={num}
                               className={`aspect-square rounded-xl flex items-center justify-center font-bold text-sm ${
                                 isMatched
                                   ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white animate-bounce'
                                   : 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white'
                               }`}
                             >
                               {num}
                             </div>
                           );
                         })}
                       </div>
                       
                       <div className="mt-3 text-xs text-gray-400">
                         {new Date(ticket.purchaseTime).toLocaleString()}
                       </div>
                       
                       {ticket.paymentMethod === 'arena' && (
                         <div className="mt-1 text-xs text-yellow-400">
                           Paid with ARENA
                         </div>
                       )}
                       
                       {isWinner && winningTicket && (
                         <div className="mt-3 p-3 bg-green-900/20 rounded-xl border border-green-700">
                           <div className="text-xs text-green-400">
                             <div className="flex justify-between">
                               <span>Matches:</span>
                               <span className="font-bold">{winningTicket.matchCount} numbers</span>
                             </div>
                             <div className="flex justify-between mt-1">
                               <span>Won:</span>
                               <span className="font-bold">
                                 {winningTicket.winAmount} ARENA
                                 {winningTicket.matchCount === 6 && ' 🏆'}
                               </span>
                             </div>
                           </div>
                         </div>
                       )}
                     </div>
                   );
                 })}
               </div>
             )}
           </div>
         </div>
       )}

       {/* Past Draws Modal - Mobile Optimized */}
       {showPastDraws && (
         <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 overflow-auto">
           <div className="bg-gradient-to-b from-gray-900 to-gray-800 backdrop-blur-lg rounded-2xl p-6 w-full max-w-sm border border-gray-700 max-h-[90vh] overflow-y-auto shadow-2xl">
             <div className="flex justify-between items-center mb-4">
               <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-pink-400 to-red-400">Past Draws</h2>
               <button 
                 onClick={() => {
                   playSound('BUTTON_CLICK');
                   setShowPastDraws(false);
                 }}
                 className="text-gray-300 hover:text-white p-2"
               >
                 <XMarkIcon className="h-5 w-5" />
               </button>
             </div>
             
{isPastDrawsLoading ? (
  <div className="text-center text-gray-400 py-12">
    <div className="w-12 h-12 border-4 border-gray-600 border-t-cyan-400 rounded-full animate-spin mx-auto mb-4"></div>
    <p>Loading past draws...</p>
  </div>
) : pastDraws.length === 0 ? (
  <div className="text-center text-gray-400 py-12">
    <ChartBarIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
    <p>No past draws yet</p>
  </div>
) : (
<div className="space-y-3">
                 {pastDraws
                   .slice(0, showAllDraws ? pastDraws.length : 3)
                   .map((draw, index) => (
                   <div key={draw.id || index} className="bg-gray-800/50 p-4 rounded-xl border border-gray-700">
                     <div className="flex justify-between items-center mb-3">
                       <span className="text-sm text-gray-400">{new Date(draw.drawTime).toLocaleDateString()}</span>
                       <span className="text-xs text-yellow-400">{draw.potAmount} ARENA</span>
                     </div>
                     <div className="grid grid-cols-6 gap-2 mb-3">
                       {draw.winningNumbers.map(num => (
                         <div 
                           key={num}
                           className="aspect-square rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 flex items-center justify-center text-white font-bold text-sm"
                         >
                           {num}
                         </div>
                       ))}
                     </div>
                     
                     {/* User's tickets for this draw */}
                     {draw.userTickets && draw.userTickets.length > 0 && (
                       <div className="mt-3 pt-3 border-t border-gray-700">
                         <div className="flex items-center justify-between mb-2">
                           <div className="text-xs text-gray-400">
                             Your tickets: {draw.userTickets.length}
                           </div>
                           {draw.userTickets.length > 1 && (
                             <button
                               onClick={() => toggleDrawExpansion(draw.id)}
                               className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                             >
                               {expandedDraws[draw.id] ? (
                                 <>
                                   <ChevronUpIcon className="h-3 w-3" />
                                   Hide
                                 </>
                               ) : (
                                 <>
                                   <ChevronDownIcon className="h-3 w-3" />
                                   Show all
                                 </>
                               )}
                             </button>
                           )}
                         </div>
                         <div className="space-y-2">
                           {draw.userTickets
                             .slice(0, expandedDraws[draw.id] ? draw.userTickets.length : 1)
                             .map((ticket, ticketIndex) => {
                               const isWinner = ticket.matches >= 3;
                               return (
                                 <div 
                                   key={ticket.id || ticketIndex}
                                   className={`p-2 rounded-lg ${
                                     isWinner 
                                       ? 'bg-green-900/20 border border-green-700/50' 
                                       : 'bg-gray-900/50'
                                   }`}
                                 >
                                   {draw.userTickets.length > 1 && (
                                     <div className="text-xs text-gray-500 mb-1">
                                       Ticket {ticketIndex + 1}
                                     </div>
                                   )}
                                   <div className="grid grid-cols-6 gap-1 mb-1">
                                     {ticket.numbers.map(num => {
                                       const isMatched = draw.winningNumbers.includes(num);
                                       return (
                                         <div 
                                           key={num}
                                           className={`aspect-square rounded text-xs flex items-center justify-center font-medium ${
                                             isMatched
                                               ? 'bg-green-600 text-white'
                                               : 'bg-gray-700 text-gray-300'
                                           }`}
                                         >
                                           {num}
                                         </div>
                                       );
                                     })}
                                   </div>
                                   {isWinner && (
                                     <div className="text-xs mt-1">
                                       <span className="text-green-400">
                                         {ticket.matches} matches • Won {ticket.winAmount} ARENA
                                       </span>
                                     </div>
                                   )}
                                 </div>
                               );
                             })}
                           {!expandedDraws[draw.id] && draw.userTickets.length > 1 && (
                             <div className="text-xs text-center text-gray-500">
                               +{draw.userTickets.length - 1} more ticket{draw.userTickets.length > 2 ? 's' : ''}
                             </div>
                           )}
                         </div>
                       </div>
                     )}
                     
                     {draw.hadJackpotWinner && (
                       <div className="text-xs text-green-400 mt-3">
                         🏆 Jackpot won{draw.jackpotWinnerCount > 1 ? ` (${draw.jackpotWinnerCount} winners)` : ''}!
                       </div>
                     )}
                   </div>
                 ))}
                 
                 {/* Show more/less draws button */}
                 {pastDraws.length > 3 && (
                   <button
                     onClick={() => setShowAllDraws(!showAllDraws)}
                     className="w-full py-3 bg-gray-800/30 hover:bg-gray-800/50 rounded-xl border border-gray-700 text-cyan-400 hover:text-cyan-300 transition-all flex items-center justify-center gap-2 text-sm"
                   >
                     {showAllDraws ? (
                       <>
                         <ChevronUpIcon className="h-4 w-4" />
                         Show less
                       </>
                     ) : (
                       <>
                         <ChevronDownIcon className="h-4 w-4" />
                         Show {pastDraws.length - 3} more draws
                       </>
                     )}
                   </button>
                 )}
               </div>
             )}
           </div>
         </div>
       )}

       {/* User Stats Modal - Mobile Optimized */}
       {showUserStats && (
         <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 overflow-auto">
           <div className="bg-gradient-to-b from-gray-900 to-gray-800 backdrop-blur-lg rounded-2xl p-6 w-full max-w-sm border border-gray-700 max-h-[90vh] overflow-y-auto shadow-2xl">
             <div className="flex justify-between items-center mb-4">
               <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-pink-400 to-red-400">Player Stats</h2>
               <button 
                 onClick={() => {
                   playSound('BUTTON_CLICK');
                   setShowUserStats(false);
                 }}
                 className="text-gray-300 hover:text-white p-2"
               >
                 <XMarkIcon className="h-5 w-5" />
               </button>
             </div>
             
             <div className="flex flex-col items-center mb-6">
               <Avatar
                 seed={user?.referralCode || user?.emailPrefix || 'guest'}
                 size={80}
                 className="mb-3"
               />
               <div className="text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                 {user?.emailPrefix || 'Guest'}
               </div>
               <div className="text-sm text-gray-300 mt-1">
                 {formatCurrency(user?.chips || 0)} Chips
               </div>
{connected && publicKey && (
  <div className="text-xs text-gray-400 mt-2">
    {publicKey.toString().slice(0, 6)}...{publicKey.toString().slice(-4)}
  </div>
)}
             </div>
             
             <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-4 rounded-xl shadow-lg mb-4 border border-gray-700">
               <div className="text-sm text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500 font-semibold mb-3">
                 LOTTERY STATS
               </div>
               <div className="space-y-2 text-sm">
                 <div className="flex justify-between items-center">
                   <span className="text-gray-400">Total Tickets:</span>
                   <span className="text-blue-300 font-mono">{user?.totalLotteryTickets || 0}</span>
                 </div>
                 <div className="flex justify-between items-center">
                   <span className="text-gray-400">Total Wins:</span>
                   <span className="text-green-300 font-mono">{user?.lotteryWins || 0}</span>
                 </div>
                 <div className="flex justify-between items-center">
                   <span className="text-gray-400">Biggest Win:</span>
                   <span className="text-pink-300 font-mono">{user?.largestLotteryWin || 0} ARENA</span>
                 </div>
                 <div className="flex justify-between items-center">
                   <span className="text-gray-400">Total Won:</span>
                   <span className="text-violet-300 font-mono">{user?.totalLotteryWinnings || 0} ARENA</span>
                 </div>
               </div>
             </div>
             
             {connected && publicKey && (
               <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-4 rounded-xl shadow-lg border border-gray-700">
                 <div className="text-sm text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500 font-semibold mb-3">
                   WALLET INFO
                 </div>
                 <div className="space-y-2 text-sm">

                   <div className="flex justify-between items-center">
                     <span className="text-gray-400">ARENA Balance:</span>
                     <span className="text-yellow-300 font-mono">{arenaBalance.toFixed(2)} ARENA</span>
                   </div>
                   <div className="flex justify-between items-center">
                     <span className="text-gray-400">SOL Balance:</span>
                     <span className="text-blue-300 font-mono">{solBalance.toFixed(4)} SOL</span>
                   </div>
                 </div>
               </div>
             )}
           </div>
         </div>
       )}
     </div>
   </div>
 );
}
